import * as React from 'react';
import { Button, Colors, Display } from 'Views/Components/Button/Button';

/**
 * Shared "Clear filters" control for the staff-table filter bars.
 *
 * Each filter bar computes how many of its filters differ from that table's defaults
 * (via countActiveFilters below) and renders this button; the button hides itself when
 * nothing is active, so being on a default view shows no extra chrome.
 */
export interface ClearFiltersButtonProps {
	/** How many filters currently differ from the table defaults. The button hides when 0. */
	activeCount: number;
	/** Reset the store to its defaults and clear the filter bar's local search text. */
	onClear: () => void;
	/** data-test-id for the rendered button, e.g. 'refunds-clear-filters'. */
	dataTestId: string;
}

// Paging and sorting are not filters: being on page 3, or having re-sorted a column, should
// never make the "Clear filters" control appear.
const IGNORED_FILTER_KEYS = ['pageNo', 'orderBy', 'orderByDescending'];

const isEmptyFilterValue = (value: unknown) => value === undefined || value === null || value === '';

const filterValuesMatch = (current: unknown, fallback: unknown): boolean => {
	// An absent search string and an empty one are the same "no filter".
	if (isEmptyFilterValue(current) && isEmptyFilterValue(fallback)) {
		return true;
	}
	if (Array.isArray(current) && Array.isArray(fallback)) {
		if (current.length !== fallback.length) {
			return false;
		}
		const sortedCurrent = current.map(String).sort();
		const sortedFallback = fallback.map(String).sort();
		return sortedCurrent.every((value, index) => value === sortedFallback[index]);
	}
	if (current instanceof Date && fallback instanceof Date) {
		return current.getTime() === fallback.getTime();
	}
	return current === fallback;
};

/**
 * Counts how many entries of a filters object differ from that table's defaults, ignoring
 * paging / sorting. Selections are compared order-insensitively so re-picking the same
 * statuses in a different order does not read as "active".
 */
export const countActiveFilters = (filters: object, defaults: object): number => {
	const current = filters as Record<string, unknown>;
	const fallback = defaults as Record<string, unknown>;
	const keys = Array.from(new Set([...Object.keys(current), ...Object.keys(fallback)]));

	return keys.filter(key => !IGNORED_FILTER_KEYS.includes(key) && !filterValuesMatch(current[key], fallback[key]))
		.length;
};

export function ClearFiltersButton({ activeCount, onClear, dataTestId }: ClearFiltersButtonProps) {
	if (activeCount <= 0) {
		return null;
	}

	return (
		<Button
			newStyles
			display={Display.Outline}
			colors={Colors.Secondary}
			onClick={onClear}
			buttonProps={{ 'data-test-id': dataTestId } as any}
		>
			Clear filters ({activeCount})
		</Button>
	);
}

export default ClearFiltersButton;
